import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from './dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Editor } from '@tiptap/react';
import { 
    CSSProperty, 
    NodePathItem, 
    parseStyles, 
    formatStyles, 
    SAFE_CORE_CSS,
    VALUE_SUGGESTIONS,
    UNIT_SUGGESTIONS,
    isColorProperty,
    BASIC_COLORS,
    PRIMEREACT_THEME_VARS,
    extractColorFromValue,
    isColor,
} from "../../general/utilities";

interface StyleInspectorModalProps {
    editor: Editor | null;
    isOpen: boolean;
    onClose: () => void;
}

interface ExtendedCSSProperty extends CSSProperty {
    important?: boolean;
}

export function StyleInspectorModal({ editor, isOpen, onClose }: StyleInspectorModalProps) {
    const [selectedNodePath, setSelectedNodePath] = useState<NodePathItem[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [properties, setProperties] = useState<ExtendedCSSProperty[]>([]);
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
    const [filteredValueSuggestions, setFilteredValueSuggestions] = useState<string[]>([]);

    useEffect(() => {
        if (!isOpen || !editor) return;
        const { $from } = editor.state.selection;
        const path: NodePathItem[] = [];
        for (let d = $from.depth; d > 0; d--) {
            const node = $from.node(d);
            path.push({
                name: node.type.name,
                pos: $from.before(d),
                depth: d,
                attributes: node.attrs
            });
        }
        const naturalPath = path.reverse();
        setSelectedNodePath(naturalPath);
        setActiveIndex(naturalPath.length - 1); 
    }, [isOpen, editor]);

    useEffect(() => {
        const activeNode = selectedNodePath[activeIndex];
        if (activeNode) {
            const parsed = parseStyles(activeNode.attributes?.style || '');
            const extended = parsed.map(p => ({
                ...p,
                important: p.value.includes('!important'),
                value: p.value.replace(/\s*!important/g, '').trim()
            }));
            setProperties(extended);
        }
    }, [activeIndex, selectedNodePath]);

    const updateProperty = (index: number, field: keyof ExtendedCSSProperty, value: any) => {
        const next = [...properties];
        next[index] = { ...next[index], [field]: value };
        setProperties(next);
    };

    const handleSave = () => {
        if (!editor || !selectedNodePath[activeIndex]) return;
        const target = selectedNodePath[activeIndex];
        const finalProperties = properties.map(p => ({
            key: p.key,
            value: p.important ? `${p.value} !important` : p.value
        }));
        const styleString = formatStyles(finalProperties);
        const { state, view } = editor;
        const transaction = state.tr.setNodeMarkup(target.pos, undefined, {
            ...target.attributes,
            style: styleString
        });
        view.dispatch(transaction);
        editor.commands.focus();
        onClose();
    };

    const searchStyles = (event: { query: string }) => {
        const query = event.query.toLowerCase();
        
        // Get a list of keys currently in the table to avoid duplicates
        const existingKeys = properties.map(p => p.key.toLowerCase());

        const suggestions = SAFE_CORE_CSS.filter(item => 
            item.toLowerCase().includes(query) && 
            !existingKeys.includes(item.toLowerCase()) // Filter out what's already there
        );
        
        setFilteredSuggestions(suggestions);
    };

    const propertyTemplate = (rowData: ExtendedCSSProperty, options: any) => {
        return (
            <AutoComplete
                value={rowData.key}
                suggestions={filteredSuggestions}
                completeMethod={searchStyles}
                onChange={(e) => updateProperty(options.rowIndex, 'key', e.value)}
                minLength={1} 
                delay={100}
                placeholder="CSS property..."
                className="w-full"
                inputClassName="w-full p-inputtext-sm font-mono border-none bg-transparent shadow-none"
                appendTo="self"
                emptyMessage="No matching properties"
            />
        );
    };

    const searchValues = (event: { query: string }, rowData: ExtendedCSSProperty) => {
        const query = event.query.toLowerCase();
        const key = rowData.key?.toLowerCase();
        
        let baseList = UNIT_SUGGESTIONS;

        if (isColorProperty(key)) {
            // Combine MDN colors with Theme Variables
            // We map them to var() syntax so they are valid CSS values immediately
            const themeVars = PRIMEREACT_THEME_VARS.map(v => `var(${v})`);
            baseList = [...themeVars, ...BASIC_COLORS];
        } else if (VALUE_SUGGESTIONS[key]) {
            baseList = VALUE_SUGGESTIONS[key];
        }

        const suggestions = baseList.filter(val => 
            val.toLowerCase().includes(query)
        );
        
        setFilteredValueSuggestions(suggestions);
    };

    const valueTemplate = (rowData: ExtendedCSSProperty, options: any) => {
        const iscolor: boolean = isColor(rowData.key, rowData.value);
        const extractedColor: string | null = extractColorFromValue(rowData.value);

        // Template for the dropdown items (the list)
        const itemTemplate = (item: string) => {
            const isVar = item.startsWith('var(');
            return (
                <div className="flex align-items-center gap-2 py-1">
                    <div 
                        style={{ 
                            backgroundColor: item, // Browser resolves the var() here!
                            width: '12px', 
                            height: '12px', 
                            border: '1px solid rgba(0,0,0,0.2)' 
                        }} 
                        className="border-round-sm"
                    />
                    <span className={`text-xs font-mono ${isVar ? 'text-primary font-bold' : ''}`}>
                        {item}
                    </span>
                </div>
            );
        };

        return (
            <div className="relative flex align-items-center w-full">
                {(iscolor || extractedColor) && rowData.value && (
                    <div 
                        style={{ 
                            backgroundColor: rowData.value, 
                            width: '14px', 
                            height: '14px', 
                            border: '1px solid #ddd',
                            position: 'absolute',
                            left: '8px',
                            zIndex: 1
                        }}
                        className="border-round-sm pointer-events-none"
                    />
                )}
                
                <AutoComplete
                    value={rowData.value}
                    suggestions={filteredValueSuggestions}
                    completeMethod={(e) => searchValues(e, rowData)}
                    onChange={(e) => updateProperty(options.rowIndex, 'value', e.value)}
                    itemTemplate={itemTemplate} // Shows swatch in the dropdown
                    placeholder="Value..."
                    className="w-full"
                    inputStyle={{ paddingLeft: (iscolor || extractedColor) ? '28px' : '8px' }}
                    inputClassName="w-full p-inputtext-sm font-mono border-none bg-transparent shadow-none"
                    appendTo="self"
                    minLength={1} 
                    delay={100}
                    scrollHeight="350px"
                />
            </div>
        );
    };

    return (
        <Dialog 
            header="CSS style inspector" 
            visible={isOpen} 
            style={{ width: '800px', height: '90vh' }}
            onHide={onClose}
            modal
            draggable={false}
            resizable={false}
            contentStyle={{ display: 'flex', flexDirection: 'column' }} // Critical for inner layout
            footer={(
                <div className="flex justify-content-end gap-2">
                    <Button label="Cancel" className="p-button-text pi-times" onClick={onClose} />
                    <Button label="Apply styles" onClick={handleSave} />
                </div>
            )}
        >
            <div className="flex flex-column h-full gap-4 py-2">
                {/* 1. Hierarchy - Fixed height area */}
                <div className="flex flex-column gap-2 flex-shrink-0">
                    <label className="text-xs font-bold text-500 uppercase tracking-wider">Hierarchy path</label>
                    <div className="flex flex-wrap align-items-center surface-100 p-2 border-round border-1 border-300 gap-2 overflow-y-auto" style={{ maxHeight: '80px' }}>
                        {selectedNodePath.map((node, idx) => (
                            <Button 
                                key={idx}
                                label={node.name}
                                className={`p-button-sm uppercase text-xs px-2 py-1 ${activeIndex === idx ? 'p-button-primary' : 'p-button-secondary p-button-text'}`}
                                onClick={() => setActiveIndex(idx)}
                            />
                        ))}
                    </div>
                </div>

                {/* 2. Style Section - This fills the remaining dialog height */}
                <div className="flex flex-column flex-grow-1 overflow-hidden gap-2">
                    <label className="text-xs font-bold text-500 uppercase tracking-wider">
                        Styles for <span className="text-primary">{selectedNodePath[activeIndex]?.name}</span>
                    </label>
                    
                    <div className="flex-grow-1 border-1 surface-border border-round overflow-hidden flex flex-column shadow-1">
                        <DataTable 
                            value={properties} 
                            size="small" 
                            scrollable 
                            scrollHeight="flex" // Forces table to fill the div
                            className="flex-grow-1"
                        >
                            <Column 
                                header="Property" 
                                body={propertyTemplate}
                                style={{ width: '30%' }} 
                            />

                            <Column 
                                header="Value" 
                                body={valueTemplate}
                                style={{ width: '40%' }} 
                            />

                            <Column header="Force (!)" body={(rowData, options) => (
                                <div className="flex justify-content-center">
                                    <Checkbox 
                                        onChange={e => updateProperty(options.rowIndex, 'important', e.checked)} 
                                        checked={rowData.important} 
                                    />
                                </div>
                            )} style={{ width: '15%', textAlign: 'center' }} />

                            <Column body={(_, options) => (
                                <Button 
                                    icon="pi pi-trash" 
                                    className="p-button-danger p-button-text p-button-sm" 
                                    onClick={() => setProperties(properties.filter((_, i) => i !== options.rowIndex))} 
                                />
                            )} style={{ width: '10%', textAlign: 'center' }} />
                        </DataTable>
                    </div>
                    
                    <div className="flex-shrink-0">
                        <Button 
                            label="Add property" 
                            icon="pi pi-plus" 
                            className="p-button-text p-button-sm w-fit mt-1" 
                            onClick={() => setProperties([...properties, { key: '', value: '', important: false }])} 
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
}